import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpenText,
  CalendarDays,
  Clock,
  Loader2,
  Mail,
  Megaphone,
  Play,
  RefreshCw,
  Sparkles,
  Trash2,
  Users,
  UserCheck,
  BarChart3,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { api, type ApiResponse } from "@/api/client";
import {
  studioKeys,
  type CalendarEntry,
  type Directive,
  type GenerationRun,
  type Persona,
  type StudioCampaign,
} from "@/components/newsletter/studioTypes";
import SubscribersPanel from "@/components/newsletter/SubscribersPanel";
import SegmentsPanel from "@/components/newsletter/SegmentsPanel";
import QueuePanel from "@/components/newsletter/QueuePanel";
import ResultsPanel from "@/components/newsletter/ResultsPanel";
import RelatedItemsPicker from "@/components/common/RelatedItemsPicker";

/**
 * Newsletter studio (Řez 6) — the operator's ONLY home for everything newsletter: Monday
 * drafts board (pick of 3) + manual campaigns, send queue, directives, personas, brand voice,
 * marketing calendar, results, subscribers and AI segments (the former standalone /newsletter
 * and /segments pages were folded in here — block 4.15 řez 3).
 */

type Tab =
  | "drafts"
  | "queue"
  | "directives"
  | "personas"
  | "voice"
  | "calendar"
  | "results"
  | "subscribers"
  | "segments";

const TABS: Array<{ key: Tab; label: string; icon: typeof Sparkles }> = [
  { key: "drafts", label: i18n.t("newsletter.studio.tabs.drafts"), icon: Sparkles },
  { key: "queue", label: i18n.t("newsletter.studio.tabs.queue"), icon: Clock },
  { key: "directives", label: i18n.t("newsletter.studio.tabs.directives"), icon: Megaphone },
  { key: "personas", label: i18n.t("newsletter.studio.tabs.personas"), icon: Users },
  { key: "voice", label: i18n.t("newsletter.studio.tabs.voice"), icon: BookOpenText },
  { key: "calendar", label: i18n.t("newsletter.studio.tabs.calendar"), icon: CalendarDays },
  { key: "results", label: i18n.t("newsletter.studio.tabs.results"), icon: BarChart3 },
  { key: "subscribers", label: i18n.t("newsletter.studio.tabs.subscribers"), icon: UserCheck },
  { key: "segments", label: i18n.t("newsletter.studio.tabs.segments"), icon: Mail },
];

export default function StudioPage() {
  const { t } = useTranslation();
  // Deep-linkable tab (?tab=segments) — the retired /segments and /newsletter routes redirect here.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab") as Tab | null;
  const tab: Tab = urlTab && TABS.some((t) => t.key === urlTab) ? urlTab : "drafts";
  const setTab = (next: Tab) =>
    setSearchParams(next === "drafts" ? {} : { tab: next }, { replace: true });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("newsletter.studio.title")}</h1>
          <p className="text-sm text-gray-500">{t("newsletter.studio.subtitle")}</p>
        </div>
        <GenerateNowButton />
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "drafts" && <DraftsBoard />}
      {tab === "queue" && <QueuePanel />}
      {tab === "directives" && <DirectivesPanel />}
      {tab === "personas" && <PersonasPanel />}
      {tab === "voice" && <BrandVoicePanel />}
      {tab === "calendar" && <CalendarPanel />}
      {tab === "results" && <ResultsPanel />}
      {tab === "subscribers" && <SubscribersPanel />}
      {tab === "segments" && <SegmentsPanel />}
    </div>
  );
}

// ── Generate now + run progress ─────────────────────────────────────────────────────────

const MAX_MUST_INCLUDE = 6; // API slices to 6 too

function GenerateNowButton() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [directive, setDirective] = useState("");
  const [tripIds, setTripIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const { data: runsData } = useQuery({
    queryKey: ["studio-runs"],
    queryFn: () => api.get<ApiResponse<GenerationRun[]>>("/api/admin/newsletter-studio/runs"),
    refetchInterval: (q) =>
      q.state.data?.data?.some((r) => r.status === "running") ? 5000 : 60000,
  });
  const latest = runsData?.data?.[0];

  // Only published trips — the orchestrator validates must-include against the published
  // catalogue, so anything else would be silently dropped.
  const { data: tripsData } = useQuery({
    queryKey: ["admin-trips"],
    queryFn: () =>
      api.get<ApiResponse<Array<{ id: string; title?: string; slug?: string; status?: string }>>>(
        "/api/admin/trips",
      ),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const publishedTrips = (tripsData?.data ?? []).filter((t) => t.status === "published");

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post("/api/admin/newsletter-studio/generate", {
        directive: directive.trim() || undefined,
        mustIncludeTripIds: tripIds.length > 0 ? tripIds : undefined,
      }),
    onSuccess: () => {
      toast.success(t("newsletter.generate.started"));
      setOpen(false);
      setDirective("");
      setTripIds([]);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["studio-runs"] }), 2000);
    },
    onError: (e: Error) => toast.error(e.message || t("newsletter.generate.startFailed")),
  });

  const runStatusLabels: Record<GenerationRun["status"], string> = {
    running: t("newsletter.generate.runStatus.running"),
    done: t("newsletter.generate.runStatus.done"),
    failed: t("newsletter.generate.runStatus.failed"),
    aborted: t("newsletter.generate.runStatus.aborted"),
  };

  return (
    <div className="relative flex items-center gap-3">
      {latest && (
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            latest.status === "running"
              ? "bg-blue-100 text-blue-700"
              : latest.status === "done"
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
          }`}
          title={latest.error ?? undefined}
        >
          {latest.isoWeek}: {runStatusLabels[latest.status]}
          {latest.status === "running" &&
            ` · ${t("newsletter.generate.tokens", { n: Math.round(latest.tokensUsed / 1000) })}`}
        </span>
      )}
      <button onClick={() => setOpen(!open)} className="btn-primary flex items-center gap-1.5">
        <Sparkles className="h-4 w-4" />
        {t("newsletter.generate.generateNow")}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-[26rem] space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("newsletter.generate.directiveLabel")}
            </label>
            <textarea
              value={directive}
              onChange={(e) => setDirective(e.target.value)}
              placeholder={t("newsletter.generate.directivePlaceholder")}
              rows={2}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              autoFocus
            />
          </div>
          <div>
            <RelatedItemsPicker
              items={publishedTrips}
              selectedIds={tripIds}
              onChange={(ids) => setTripIds(ids.slice(0, MAX_MUST_INCLUDE))}
              label={t("newsletter.generate.mustIncludeLabel", { max: MAX_MUST_INCLUDE })}
              placeholder={t("newsletter.generate.mustIncludePlaceholder")}
            />
            <p className="mt-2 text-xs text-gray-500">{t("newsletter.generate.mustIncludeHint")}</p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setOpen(false)}
              className="btn-secondary"
              disabled={generateMutation.isPending}
            >
              {t("common.actions.cancel")}
            </button>
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="btn-primary flex items-center gap-1.5"
            >
              {generateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {t("newsletter.generate.start")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Drafts board ────────────────────────────────────────────────────────────────────────

function DraftsBoard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: studioKeys.drafts,
    queryFn: () => api.get<ApiResponse<StudioCampaign[]>>("/api/admin/newsletter/campaigns"),
    refetchInterval: 30000,
  });

  const all = data?.data ?? [];
  const campaigns = all.filter((c) => c.sectionsJson);
  const drafts = campaigns.filter((c) => c.status === "generated" || c.status === "draft");
  // Hand-written (non-sections) campaigns: drafts live here; scheduled show in Queue, sent in
  // Results — together that covers everything the retired /newsletter list used to.
  const manualDrafts = all.filter((c) => !c.sectionsJson && c.status === "draft");
  const latestRunId = drafts[0]?.generationRunId;
  const latestBatch = latestRunId
    ? drafts.filter((d) => d.generationRunId === latestRunId)
    : drafts.slice(0, 3);
  const older = drafts.filter((d) => !latestBatch.includes(d));

  // Opening a draft is just browsing — the "why this one?" learning signal is captured in the
  // Send dialog instead, at the moment a draft is actually chosen.
  const pickDraft = (c: StudioCampaign) => navigate(`/newsletter-studio/edition/${c.id}`);

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {drafts.length === 0 ? (
        <div className="card py-12 text-center text-gray-500">
          {t("newsletter.drafts.noDraftsPrefix")}{" "}
          <strong>{t("newsletter.generate.generateNow")}</strong>{" "}
          {t("newsletter.drafts.noDraftsSuffix")}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {latestBatch.map((c) => (
            <DraftCard key={c.id} campaign={c} onPick={() => pickDraft(c)} />
          ))}
        </div>
      )}
      {older.length > 0 && (
        <div className="card">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">
            {t("newsletter.drafts.olderDrafts")}
          </h3>
          <div className="divide-y divide-gray-100">
            {older.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/newsletter-studio/edition/${c.id}`)}
                className="flex w-full items-center justify-between py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="truncate">{c.subject}</span>
                <span className="text-xs text-gray-400">{c.createdAt.slice(0, 10)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <ManualCampaignsCard campaigns={manualDrafts} />
    </div>
  );
}

/** Hand-written one-off campaigns (legacy editor) — kept alive under the studio roof. */
function ManualCampaignsCard({ campaigns }: { campaigns: StudioCampaign[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/newsletter/campaigns/${id}`),
    onSuccess: () => {
      toast.success(t("newsletter.drafts.deleted"));
      queryClient.invalidateQueries({ queryKey: studioKeys.drafts });
    },
    onError: () => toast.error(t("newsletter.drafts.deleteFailed")),
  });

  return (
    <div className="card">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          {t("newsletter.drafts.manualTitle")}
        </h3>
        <button onClick={() => navigate("/newsletter/editor/new")} className="btn-secondary">
          <Mail className="h-4 w-4" />
          {t("newsletter.drafts.newManual")}
        </button>
      </div>
      {campaigns.length === 0 ? (
        <p className="py-3 text-sm text-gray-500">{t("newsletter.drafts.manualEmpty")}</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {campaigns.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2 text-sm">
              <button
                onClick={() => navigate(`/newsletter/editor/${c.id}`)}
                className="min-w-0 flex-1 truncate text-left hover:underline"
              >
                {c.subject}
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">{c.createdAt.slice(0, 10)}</span>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        t("newsletter.drafts.confirmDeleteDraft", { subject: c.subject }),
                      )
                    ) {
                      del.mutate(c.id);
                    }
                  }}
                  className="text-error-600 hover:bg-error-50 rounded p-1"
                  title={t("newsletter.drafts.deleteDraftTitle")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Email preview thumbnail: the email is a fixed 640px-wide document, the card column is not —
 * measure the container and scale 640 → container width so the whole email width is visible
 * (the old fixed 1280px viewport + scale-50 left the right half cut off).
 */
function EmailPreviewFrame({ title, html }: { title: string; html: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(Math.max(0.1, el.clientWidth / 640));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="h-full w-full">
      <iframe
        title={title}
        srcDoc={html}
        sandbox=""
        className="border-0"
        style={{
          width: 640,
          height: 288 / scale, // fill the 288px (h-72) window after scaling
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function DraftCard({ campaign, onPick }: { campaign: StudioCampaign; onPick: () => void }) {
  const { t } = useTranslation();
  // Screenshots are stored as newsletter-runs/<runId>/draft<i>-r<round>-light.jpg — we don't
  // know i/round here, so the card shows the live HTML preview instead (cheap iframe).
  const { data } = useQuery({
    queryKey: ["draft-preview", campaign.id],
    queryFn: () =>
      api.post<ApiResponse<{ html: string }>>("/api/admin/newsletter-studio/preview", {
        subject: campaign.subject,
        preheader: campaign.preheader ?? campaign.subject,
        themeKey: campaign.themeKey ?? "violet",
        subjectVariants: campaign.subjectVariants ?? [],
        sections: campaign.sectionsJson,
        campaignId: campaign.id,
      }),
    staleTime: 10 * 60_000,
  });

  return (
    <div className="card flex flex-col !p-0">
      <div className="h-72 overflow-hidden rounded-t-xl bg-gray-100">
        {data?.data?.html ? (
          <EmailPreviewFrame title={campaign.subject} html={data.data.html} />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-300">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-violet-600">
          {campaign.aiPrompt ?? t("newsletter.drafts.draftFallback")}
        </p>
        <h3 className="font-semibold leading-snug text-gray-900">{campaign.subject}</h3>
        <p className="line-clamp-2 text-sm text-gray-500">{campaign.preheader}</p>
        <div className="flex-1" />
        <button onClick={onPick} className="btn-primary w-full">
          {t("newsletter.drafts.pickAndEdit")}
        </button>
      </div>
    </div>
  );
}

// ── Directives ──────────────────────────────────────────────────────────────────────────

function DirectivesPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["studio-directives"],
    queryFn: () => api.get<ApiResponse<Directive[]>>("/api/admin/newsletter-studio/directives"),
  });
  const [kind, setKind] = useState<"inspiration" | "assignment">("inspiration");
  const [text, setText] = useState("");
  const [verbatim, setVerbatim] = useState("");

  const createMutation = useMutation({
    mutationFn: () =>
      api.post("/api/admin/newsletter-studio/directives", {
        kind,
        text,
        verbatimText: verbatim.trim() || null,
        verbatimSection: verbatim.trim() ? "note" : null,
      }),
    onSuccess: () => {
      toast.success(t("newsletter.directives.added"));
      setText("");
      setVerbatim("");
      queryClient.invalidateQueries({ queryKey: ["studio-directives"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/newsletter-studio/directives/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["studio-directives"] }),
  });

  const rows = data?.data ?? [];

  const kindLabels: Record<Directive["kind"], string> = {
    inspiration: t("newsletter.directives.kinds.inspiration"),
    assignment: t("newsletter.directives.kinds.assignment"),
  };
  const statusLabels: Record<Directive["status"], string> = {
    pending: t("newsletter.directives.statuses.pending"),
    consumed: t("newsletter.directives.statuses.consumed"),
    archived: t("newsletter.directives.statuses.archived"),
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card space-y-3">
        <h3 className="font-semibold text-gray-900">{t("newsletter.directives.newTitle")}</h3>
        <div className="flex gap-2">
          {(["inspiration", "assignment"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`rounded-full px-3 py-1 text-sm ${kind === k ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600"}`}
            >
              {k === "inspiration"
                ? t("newsletter.directives.kindInspiration")
                : t("newsletter.directives.kindAssignment")}
            </button>
          ))}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={
            kind === "assignment"
              ? t("newsletter.directives.assignmentPlaceholder")
              : t("newsletter.directives.inspirationPlaceholder")
          }
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <textarea
          value={verbatim}
          onChange={(e) => setVerbatim(e.target.value)}
          rows={3}
          placeholder={t("newsletter.directives.verbatimPlaceholder")}
          className="w-full rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2 text-sm"
        />
        <button
          onClick={() => createMutation.mutate()}
          disabled={!text.trim() || createMutation.isPending}
          className="btn-primary"
        >
          {t("newsletter.directives.add")}
        </button>
      </div>

      <div className="card">
        <h3 className="mb-3 font-semibold text-gray-900">{t("newsletter.directives.listTitle")}</h3>
        <div className="divide-y divide-gray-100">
          {rows.map((d) => (
            <div key={d.id} className="flex items-start gap-3 py-2.5">
              <span
                className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                  d.kind === "assignment" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                }`}
              >
                {kindLabels[d.kind]}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800">{d.text}</p>
                {d.verbatimText && (
                  <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs italic text-amber-800">
                    {t("newsletter.directives.verbatimNote", { text: d.verbatimText })}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-gray-400">
                  {statusLabels[d.status]} · {d.createdAt.slice(0, 10)}
                </p>
              </div>
              {d.status === "pending" && (
                <button
                  title={t("common.actions.archive")}
                  onClick={() => archiveMutation.mutate(d.id)}
                  className="p-1 text-gray-300 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!rows.length && (
            <p className="py-4 text-sm text-gray-400">{t("newsletter.directives.empty")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Personas ────────────────────────────────────────────────────────────────────────────

function PersonasPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["studio-personas"],
    queryFn: () => api.get<ApiResponse<Persona[]>>("/api/admin/newsletter-studio/personas"),
  });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["studio-personas"] });
  const createMutation = useMutation({
    mutationFn: () => api.post("/api/admin/newsletter-studio/personas", { name, description }),
    onSuccess: () => {
      setName("");
      setDescription("");
      invalidate();
    },
  });
  const patchMutation = useMutation({
    mutationFn: (p: { id: string; patch: Partial<Persona> }) =>
      api.put(`/api/admin/newsletter-studio/personas/${p.id}`, p.patch),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/newsletter-studio/personas/${id}`),
    onSuccess: invalidate,
  });

  const rows = data?.data ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card space-y-3">
        <h3 className="font-semibold text-gray-900">{t("newsletter.personas.newTitle")}</h3>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("newsletter.personas.namePlaceholder")}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder={t("newsletter.personas.descriptionPlaceholder")}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <button
          onClick={() => createMutation.mutate()}
          disabled={!name.trim() || !description.trim()}
          className="btn-primary"
        >
          {t("newsletter.personas.add")}
        </button>
      </div>

      <div className="card">
        <h3 className="mb-3 font-semibold text-gray-900">{t("newsletter.personas.listTitle")}</h3>
        <div className="divide-y divide-gray-100">
          {rows.map((p) => (
            <div key={p.id} className="flex items-start gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  {p.name}
                  {p.source === "discovered" && (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-700">
                      {t("newsletter.personas.discovered")}
                    </span>
                  )}
                  {p.needsReview && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                      {t("newsletter.personas.review")}
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-500">{p.description}</p>
              </div>
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={p.isActive}
                  onChange={(e) =>
                    patchMutation.mutate({
                      id: p.id,
                      patch: { isActive: e.target.checked, needsReview: false },
                    })
                  }
                />
                {t("newsletter.personas.active")}
              </label>
              <button
                title={t("common.actions.delete")}
                onClick={() => deleteMutation.mutate(p.id)}
                className="p-1 text-gray-300 hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!rows.length && (
            <p className="py-4 text-sm text-gray-400">{t("newsletter.personas.empty")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Brand voice ─────────────────────────────────────────────────────────────────────────

function BrandVoicePanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["studio-voice"],
    queryFn: () => api.get<ApiResponse<{ doc: string }>>("/api/admin/newsletter-studio/voice"),
  });
  const [doc, setDoc] = useState<string | null>(null);
  const current = doc ?? data?.data?.doc ?? "";

  const saveMutation = useMutation({
    mutationFn: () => api.put("/api/admin/newsletter-studio/voice", { doc: current }),
    onSuccess: () => toast.success(t("newsletter.voice.saved")),
  });
  const analyzeMutation = useMutation({
    mutationFn: () =>
      api.post<ApiResponse<{ doc: string }>>("/api/admin/newsletter-studio/voice/analyze", {}),
    onSuccess: (res) => {
      setDoc(res.data?.doc ?? "");
      toast.success(t("newsletter.voice.reanalyzed"));
      queryClient.invalidateQueries({ queryKey: ["studio-voice"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />;

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">{t("newsletter.voice.title")}</h3>
        <div className="flex gap-2">
          <button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            className="btn-secondary flex items-center gap-1.5"
          >
            {analyzeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {t("newsletter.voice.reanalyze")}
          </button>
          <button onClick={() => saveMutation.mutate()} className="btn-primary">
            {t("common.actions.save")}
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500">{t("newsletter.voice.hint")}</p>
      <textarea
        value={current}
        onChange={(e) => setDoc(e.target.value)}
        rows={24}
        className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm"
      />
    </div>
  );
}

// ── Calendar ────────────────────────────────────────────────────────────────────────────

function CalendarPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["studio-calendar"],
    queryFn: () => api.get<ApiResponse<CalendarEntry[]>>("/api/admin/newsletter-studio/calendar"),
  });
  const [form, setForm] = useState({ dateFrom: "", dateTo: "", label: "", note: "" });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["studio-calendar"] });
  const createMutation = useMutation({
    mutationFn: () =>
      api.post("/api/admin/newsletter-studio/calendar", { ...form, note: form.note || null }),
    onSuccess: () => {
      setForm({ dateFrom: "", dateTo: "", label: "", note: "" });
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/newsletter-studio/calendar/${id}`),
    onSuccess: invalidate,
  });

  const rows = data?.data ?? [];

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <label className="block text-xs text-gray-500">
          {t("newsletter.calendar.from")}
          <input
            type="date"
            value={form.dateFrom}
            onChange={(e) => setForm({ ...form, dateFrom: e.target.value })}
            className="mt-1 block rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-xs text-gray-500">
          {t("newsletter.calendar.to")}
          <input
            type="date"
            value={form.dateTo}
            onChange={(e) => setForm({ ...form, dateTo: e.target.value })}
            className="mt-1 block rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <input
          type="text"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder={t("newsletter.calendar.labelPlaceholder")}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder={t("newsletter.calendar.notePlaceholder")}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <button
          onClick={() => createMutation.mutate()}
          disabled={!form.dateFrom || !form.dateTo || !form.label}
          className="btn-primary"
        >
          {t("common.actions.add")}
        </button>
      </div>
      <div className="divide-y divide-gray-100">
        {rows.map((e) => (
          <div key={e.id} className="flex items-center gap-3 py-2 text-sm">
            <span className="w-44 font-mono text-xs text-gray-500">
              {e.dateFrom} → {e.dateTo}
            </span>
            <span className="font-medium text-gray-800">{e.label}</span>
            <span className="flex-1 truncate text-gray-500">{e.note}</span>
            <button
              title={t("common.actions.delete")}
              onClick={() => deleteMutation.mutate(e.id)}
              className="p-1 text-gray-300 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!rows.length && (
          <p className="py-4 text-sm text-gray-400">{t("newsletter.calendar.empty")}</p>
        )}
      </div>
    </div>
  );
}
